"use client";

import { ChevronDown, ChevronRight, Rocket, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { ClosingCtaSection } from "components/landing/content-sections/ClosingCtaSection";
import { ExamplesSection } from "components/landing/content-sections/ExamplesSection";
import { FAQSection } from "components/landing/content-sections/FAQSection";
import { HeroSection } from "components/landing/content-sections/HeroSection";
import { HowItWorksSection } from "components/landing/content-sections/HowItWorksSection";
import { PricingSection } from "components/landing/content-sections/PricingSection";
import { WhatYouGetSection } from "components/landing/content-sections/WhatYouGetSection";
import { Footer } from "components/landing/layout/Footer";
import { Navigation } from "components/landing/layout/Navigation";
import { LandingSchemaMarkup } from "components/landing/seo/LandingSchemaMarkup";
import { Button } from "components/products/muggleTestV0/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTitleGroup,
} from "components/products/muggleTestV0/components/ui/dialog";
import {
  HERO_CTA_PRIMARY,
  SAMPLE_REPORT_IMAGE,
  SAMPLE_REPORT_IMAGE_ALT,
  SAMPLE_REPORT_IMAGE_HEIGHT_PX,
  SAMPLE_REPORT_IMAGE_WIDTH_PX,
} from "content/landing";
import { useLandingAuthActions } from "hooks/landing/useLandingAuthActions";
import { useStickyCtaVisibility } from "hooks/landing/useStickyCtaVisibility";
import { trackCtaClick } from "lib/analytics";
import { scrollToSection } from "utils/landing/scrollUtils";

import LandingLayout from "../layout";

/** Threshold in pixels to consider "scrolled enough" to hide the indicator */
const SCROLL_INDICATOR_THRESHOLD_PX = 50;

export default function LandingPage() {
  const { signup } = useLandingAuthActions();
  const [showSampleReport, setShowSampleReport] = useState(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);
  const heroRef = useRef<HTMLDivElement>(null);
  const reportScrollRef = useRef<HTMLDivElement>(null);

  const { showFixedButton, showMobileBar } = useStickyCtaVisibility({ heroRef: heroRef });

  /**
   * Handles scroll events on the sample report modal content.
   * Hides the scroll indicator once user scrolls past threshold.
   */
  const handleReportScroll = useCallback(() => {
    if (!reportScrollRef.current) {
      return;
    }
    const { scrollTop } = reportScrollRef.current;
    setShowScrollIndicator(scrollTop < SCROLL_INDICATOR_THRESHOLD_PX);
  }, []);

  /**
   * Resets scroll indicator visibility when modal opens.
   */
  useEffect(() => {
    if (showSampleReport) {
      setShowScrollIndicator(true);
      if (reportScrollRef.current) {
        reportScrollRef.current.scrollTop = 0;
      }
    }
  }, [showSampleReport]);

  // Handle URL hash on mount
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const sectionId = hash.substring(1);
      setTimeout(() => {
        scrollToSection(sectionId);
      }, 100);
    }
  }, []);

  return (
    <LandingLayout>
      <LandingSchemaMarkup />
      <Navigation />

      {/* Fixed floating CTA button — desktop only */}
      {showFixedButton && (
        <div className="fixed bottom-6 right-6 z-50 hidden md:block">
          <Button size="lg" className="rounded-full shadow-lg hover:shadow-xl transition-shadow" onClick={() => {
            trackCtaClick("sticky_desktop");
            signup("sticky_desktop");
          }}>
            <Rocket className="mr-1 h-5 w-5" />
            <span>{HERO_CTA_PRIMARY}</span>
          </Button>
        </div>
      )}

      {/* Sticky mobile CTA bar — mobile only */}
      {showMobileBar && (
        <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-background/95 backdrop-blur border-t border-border/40 px-4 py-3 pb-safe">
          <Button
            size="lg"
            className="w-full gap-2 bg-primary text-primary-foreground hover:bg-primary/90"
            onClick={() => {
              trackCtaClick("sticky_mobile");
              signup("sticky_mobile");
            }}
          >
            {HERO_CTA_PRIMARY}
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      <main className="flex-1 pb-20 md:pb-0">
        <div ref={heroRef}>
          <HeroSection />
        </div>
        <HowItWorksSection />
        <ExamplesSection onViewSampleReport={() => setShowSampleReport(true)} />
        <WhatYouGetSection />
        <PricingSection />
        <FAQSection />
        <ClosingCtaSection onSignup={() => signup(undefined, "/muggleTestV0/dashboard/projects/create/website")} />
      </main>
      <Footer />

      <Dialog open={showSampleReport} onOpenChange={setShowSampleReport}>
        <DialogContent variant="fullscreen" size="2xl" className="xl:max-w-7xl">
          <DialogHeader className="flex-col items-stretch gap-3 space-y-0 text-left sm:flex-row sm:items-start sm:gap-4 flex-shrink-0 px-4 sm:px-6 pt-4 pb-4 border-b bg-background pr-12">
            <DialogTitleGroup>
              <DialogTitle className="text-xl font-semibold">Sample test report</DialogTitle>
              <DialogDescription className="text-xs mt-1 text-left">
                Full-length report preview - scroll to see metrics, AI insights, bugs, UX issues, and coverage.
              </DialogDescription>
            </DialogTitleGroup>
          </DialogHeader>

          {/* Scrollable content area with scroll indicator */}
          <div className="relative flex-1 min-h-0 flex flex-col">
            <div
              ref={reportScrollRef}
              onScroll={handleReportScroll}
              className="flex-1 overflow-y-auto overflow-x-hidden px-4 sm:px-6 py-4"
            >
              <div className="rounded-xl border border-border/50 bg-muted/50 p-1 shadow-inner ring-1 ring-black/[0.04] dark:ring-white/[0.06] sm:p-1.5">
                <img
                  src={SAMPLE_REPORT_IMAGE}
                  alt={SAMPLE_REPORT_IMAGE_ALT}
                  width={SAMPLE_REPORT_IMAGE_WIDTH_PX}
                  height={SAMPLE_REPORT_IMAGE_HEIGHT_PX}
                  className="mx-auto block w-full max-w-full rounded-lg bg-background shadow-sm"
                  sizes="(max-width: 1280px) 96vw, min(1280px, 96vw)"
                  decoding="async"
                />
              </div>
            </div>

            {/* Scroll indicator overlay - anchored to bottom of scroll area */}
            <div
              className={`pointer-events-none absolute inset-x-0 bottom-0 z-10 flex flex-col items-center justify-end transition-opacity duration-300 ${
                showScrollIndicator ? "opacity-100" : "opacity-0"
              }`}
            >
              {/* Gradient fade effect */}
              <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-background via-background/90 to-transparent" />
              {/* Bouncing chevron with label */}
              <div className="relative z-10 mb-5 flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 shadow-xl shadow-primary/25">
                <span className="text-sm font-semibold text-primary-foreground">Scroll for more</span>
                <ChevronDown className="h-5 w-5 animate-bounce text-primary-foreground" />
              </div>
            </div>
          </div>

          <DialogFooter className="flex-shrink-0 px-4 sm:px-6 py-4 border-t bg-background">
            <Button onClick={() => setShowSampleReport(false)} className="w-24">
              <X className="mr-1 h-4 w-4" />
              <span>Close</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </LandingLayout>
  );
}
